import { getEffectiveScalePriceCents } from '~/utils/price-utils'
import type { EffectivePriceScaleInput } from '~/utils/price-utils'

type Reference = 'office' | 'pharmacy'

/**
 * Internal, richly-typed shape used inside `buildShoppingListItem`. The public
 * `TrackAddArgs.products` accepts any product with a `handle` so each caller
 * can pass its own `Product` variant without nominal-typing friction; we cast
 * to this shape on entry and read everything defensively.
 */
type TrackedShoppingListProduct = {
  handle: string
  name?: string
  quantity?: number
  listPrice?: number | { amount?: number }
  scalePrices?: EffectivePriceScaleInput[]
  categoriesNew?: string[]
  category?: {
    // Search/PDP shape — already i18n keys
    group?: string
    name?: string
    child?: string
    // Order-history shape — i18n keys under different field names
    groupI18n?: string
    nameI18n?: string
    childI18n?: string
  }
  supplier?: { name?: string }
  manufacturer?: { name?: string }
  clinicChains?: unknown[]
  isActiveCampaign?: boolean
  campaign?: { status?: string, afterPrice?: number }
  preselectedTag?: string
  customerReferences?: readonly string[]
}

export interface ShoppingListItemArgs {
  data: TrackedShoppingListProduct
  fields?: Record<string, string>
  supplierName?: string
  reference?: Reference
  quantity?: number
}

export interface TrackAddArgs {
  products: ReadonlyArray<{ handle: string } & object>
  listName: string
  fields?: Record<string, string>
  supplierName?: string
  reference?: Reference
  quantity?: number
  pageSection?: string
}

export interface TrackRemoveArgs {
  product: { handle: string, name?: string }
  supplierName?: string
  pageSection?: string
}

export interface TrackCreateListArgs {
  pageSection?: string
}

// GA4 caps the category hierarchy at 5 levels (item_category, item_category2..5).
const MAX_CATEGORY_LEVELS = 5

/**
 * Returns the GA4-canonical category hierarchy as a key-mapped object:
 * `{ item_category, item_category2?, ..., item_category5? }`. The first level is
 * always emitted (defaulting to `''`) so downstream consumers can rely on the
 * key being present; deeper levels are only included when source data resolves
 * to a value at that level.
 */
function resolveCategoryHierarchy(
  data: TrackedShoppingListProduct,
  resolveDeepestPath: (terms: string[], lang: string) => { ancestors: { terms: Record<string, string> }[], node: { terms: Record<string, string> } } | null,
  searchLanguage: string,
  t: (key: string) => string,
): Record<string, string> {
  let levels: string[] = []

  const terms = data.categoriesNew ?? []
  if (terms.length > 0) {
    // `categoriesNew` arrives in the user's local language (e.g. German on
    // shop.heiland.com), so we look up the node via `searchLanguage`. The
    // emitted label, however, is always English so GA4 category reports stay
    // locale-independent and consolidatable across markets. Falls back to the
    // local term if the English translation is missing for that node.
    const resolved = resolveDeepestPath(terms, searchLanguage)
    if (resolved) {
      levels = [...resolved.ancestors, resolved.node]
        .map(n => n.terms.en ?? n.terms[searchLanguage])
        .filter(Boolean)
    }
  }

  // Legacy `category` ships under two field-name variants depending on the
  // surface: `{group, name, child}` (search/PDP) or `{groupI18n, nameI18n,
  // childI18n}` (order-history). Both contain i18n keys.
  if (levels.length === 0) {
    const legacyKeys = [
      data.category?.group ?? data.category?.groupI18n,
      data.category?.name ?? data.category?.nameI18n,
      data.category?.child ?? data.category?.childI18n,
    ].filter(Boolean) as string[]
    levels = legacyKeys.map(k => t(k))
  }

  const result: Record<string, string> = { item_category: levels[0] ?? '' }
  for (let i = 1; i < Math.min(levels.length, MAX_CATEGORY_LEVELS); i++) {
    result[`item_category${i + 1}`] = levels[i]
  }
  return result
}

export const useShoppingListTracking = () => {
  const gtm = useGtm()
  const treeStore = useCategoryTreeStore()
  const { searchLanguage } = useCountryConfig()
  const { $i18n } = useNuxtApp()
  const helper = useHelper()

  function buildShoppingListItem({ data, fields, supplierName, reference = 'office', quantity }: ShoppingListItemArgs) {
    const effectiveQuantity = quantity ?? data.quantity ?? 1
    return {
      item_id: data.handle,
      item_name: data.name ?? 'Unknown',
      item_brand: data.manufacturer?.name ?? 'Unknown',
      supplier_name: supplierName ?? data.supplier?.name ?? '',
      ...resolveCategoryHierarchy(data, treeStore.resolveDeepestPath, searchLanguage, $i18n.t),
      item_variant: helper.resolveItemVariant(fields),
      price: Math.round(getEffectiveScalePriceCents(data, effectiveQuantity, reference)) / 100,
      quantity: effectiveQuantity,
      promotion: (data.isActiveCampaign === true || data.campaign?.status === 'ACTIVE') ? 'yes' : 'no',
      reference_value: reference,
      gpo_recommended: (data.clinicChains?.length ?? 0) > 0 ? 'yes' : 'no',
    }
  }

  function trackAddToShoppingList({ products, listName, fields, supplierName, reference, quantity, pageSection }: TrackAddArgs) {
    if (!products?.length) return
    const items = products.map((rawProduct) => {
      const product = rawProduct as TrackedShoppingListProduct
      const fallbackReference = (product.preselectedTag ?? (product as any).customerReference ?? product.customerReferences?.[0]) as Reference | undefined
      return buildShoppingListItem({
        data: product,
        fields,
        supplierName: supplierName ?? product.supplier?.name,
        reference: reference ?? fallbackReference ?? 'office',
        quantity,
      })
    })
    const value = Math.round(items.reduce((sum, item) => sum + item.price * item.quantity, 0) * 100) / 100
    gtm?.trackEvent({ ecommerce: null })
    gtm?.trackEvent({
      event: 'add_to_wishlist',
      page_section: pageSection ?? usePageSection(),
      wishlist_type: listName === 'default' ? 'default' : 'custom',
      ecommerce: { currency: 'EUR', value, items },
    })
  }

  function trackRemoveFromShoppingList({ product, supplierName, pageSection }: TrackRemoveArgs) {
    if (!product?.handle) return
    gtm?.trackEvent({
      event: 'remove_from_wishlist',
      page_section: pageSection ?? usePageSection(),
      item_id: product.handle,
      item_name: product.name ?? '',
      supplier_name: supplierName ?? '',
    })
  }

  function trackCreateCustomShoppingList({ pageSection }: TrackCreateListArgs = {}) {
    gtm?.trackEvent({
      event: 'create_custom_shopping_list',
      page_section: pageSection ?? usePageSection(),
    })
  }

  function trackDeleteCustomShoppingList() {
    gtm?.trackEvent({ event: 'delete_custom_shopping_list' })
  }

  return { trackAddToShoppingList, trackRemoveFromShoppingList, trackCreateCustomShoppingList, trackDeleteCustomShoppingList }
}

export default useShoppingListTracking
